import React from "react";
import { gql, useQuery } from "@apollo/client";
import { withStyles } from "@material-ui/core/styles";
import TablePagination from "@material-ui/core/TablePagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  TableSortLabel,
  Typography
} from "@material-ui/core";
import TableLink from "../TableLink";

const styles = theme => ({
  root: {
    marginTop: theme.spacing(2),
    overflowX: "auto",
    margin: "auto"
  },
  table: {
    minWidth: 700
  },
  textField: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    minWidth: 300
  }
});

const GET_QUESTION_SET_TOURNAMENTS = gql`
  query tournamentQuery(
    $first: Int
    $offset: Int
    $orderBy: [_TournamentOrdering]
    $filter: _TournamentFilter
  ) {
    Tournament(
      first: $first
      offset: $offset
      orderBy: $orderBy
      filter: $filter
    ) {
      id
      name
      host
      tournament_date {
        formatted
      }
      pp20tuh
      ppb
    }
  }
`;

function TournamentList({ classes, questionSetId }) {
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("tournament_date");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const { loading, data, error } = useQuery(GET_QUESTION_SET_TOURNAMENTS, {
    variables: {
      first: rowsPerPage,
      offset: rowsPerPage * page,
      orderBy: `${orderBy}_${order}`,
      filter: { questionSet: { id: questionSetId } }
    }
  });

  const handleChangePage = (_, page) => {
    setPage(page);
  };

  const handleChangeRowsPerPage = (_, rows) => {
    setRowsPerPage(parseInt(rows.key));
  };

  const handleSortRequest = property => {
    const newOrderBy = property;
    let newOrder = "desc";

    if (orderBy === property && order === "desc") {
      newOrder = "asc";
    }

    setOrder(newOrder);
    setOrderBy(newOrderBy);
  };

  return (
    <div className={classes.root}>
      <Typography variant="h5" gutterBottom>
        Tournaments
      </Typography>
      {loading && !error && <p>Loading...</p>}
      {error && !loading && <p>Error</p>}
      {data && !loading && !error && (
        <div>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell
                  key="name"
                  sortDirection={orderBy === "tournament_name" ? order : false}
                >
                  <Tooltip
                    title="Sort"
                    placement="bottom-start"
                    enterDelay={300}
                  >
                    <TableSortLabel
                      active={orderBy === "tournament_name"}
                      direction={order}
                      onClick={() => handleSortRequest("tournament_name")}
                    >
                      Tournament
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell
                  key="tournament_date"
                  sortDirection={orderBy === "tournament_date" ? order : false}
                >
                  <Tooltip title="Sort" placement="bottom-end" enterDelay={300}>
                    <TableSortLabel
                      active={orderBy === "tournament_date"}
                      direction={order}
                      onClick={() => handleSortRequest("tournament_date")}
                    >
                      Date
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell
                  key="host"
                  sortDirection={orderBy === "host" ? order : false}
                >
                  <Tooltip title="Sort" placement="bottom-end" enterDelay={300}>
                    <TableSortLabel
                      active={orderBy === "host"}
                      direction={order}
                      onClick={() => handleSortRequest("host")}
                    >
                      Host
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell
                  key="pp20tuh"
                  sortDirection={orderBy === "pp20tuh" ? order : false}
                >
                  <Tooltip
                    title="Sort"
                    placement="bottom-start"
                    enterDelay={300}
                  >
                    <TableSortLabel
                      active={orderBy === "pp20tuh"}
                      direction={order}
                      onClick={() => handleSortRequest("pp20tuh")}
                    >
                      PP20TUH
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell
                  key="ppb"
                  sortDirection={orderBy === "ppb" ? order : false}
                >
                  <Tooltip
                    title="Sort"
                    placement="bottom-start"
                    enterDelay={300}
                  >
                    <TableSortLabel
                      active={orderBy === "ppb"}
                      direction={order}
                      onClick={() => handleSortRequest("ppb")}
                    >
                      PPB
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.Tournament.map(t => {
                return (
                  <TableRow key={t.id}>
                    <TableCell component="th" scope="row">
                      <TableLink to={`/tournament/${t.id}`}>{t.name}</TableLink>
                    </TableCell>
                    <TableCell>
                      {new Date(
                        t.tournament_date.formatted
                      ).toLocaleDateString()}
                    </TableCell>
                    <TableCell>{t.host}</TableCell>
                    <TableCell>{t.pp20tuh?.toFixed(2)}</TableCell>
                    <TableCell>{t.ppb?.toFixed(2)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={Number.MAX_VALUE}
            labelDisplayedRows={({ from, to }) => `Results ${from} - ${to}`}
            nextIconButtonProps={
              data.Tournament.length < rowsPerPage ? { disabled: true } : {}
            }
            rowsPerPage={rowsPerPage}
            page={page}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </div>
      )}
    </div>
  );
}

export default withStyles(styles)(TournamentList);
